<template>
    <div class="ft-page" :class="{ 'ft-capture': capturing }">
        <div ref="content" class="ft-inner">

            <!-- Hero -->
            <div class="ft-hero">
                <div class="ft-hero-row">
                    <h1 class="ft-hero-title">FREE TARGET</h1>
                    <div class="ft-hero-badge"><b>24</b><span>Hours</span></div>
                </div>
                <p class="ft-hero-sub">Total time calculation based on call and stream duration</p>
                <div class="ft-hero-pill">Timing extended for streaming 24/7</div>
            </div>

            <!-- The reward itself -->
            <div class="ft-reward">
                <div class="ft-reward-cap">Weekly reward</div>
                <div class="ft-reward-value">
                    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="12" cy="12" r="9.6" fill="#FFC94D" />
                        <circle cx="12" cy="12" r="6.3" fill="none" stroke="#C97A0A" stroke-width="1.7" />
                    </svg>
                    <span class="ft-num">150,000</span>
                </div>
                <p class="ft-reward-note">Coins, credited every Monday</p>
            </div>

            <!-- The three thresholds, pulled out of the prose -->
            <div class="ft-head">Rules for being eligible</div>
            <div class="ft-list">
                <div v-for="req in requirements" :key="req.value" class="ft-req">
                    <div class="ft-req-icon" v-html="req.icon"></div>
                    <div class="ft-req-body">
                        <p class="ft-req-value">{{ req.value }}</p>
                        <p class="ft-req-text">{{ req.text }}</p>
                    </div>
                </div>
            </div>

            <!-- Guidelines -->
            <div class="ft-head">Guidelines for livestreaming</div>
            <div class="ft-list">
                <div v-for="(guide, i) in guidelines" :key="i" class="ft-guide">
                    <span class="ft-guide-dot">{{ i + 1 }}</span>
                    <p>{{ guide }}</p>
                </div>
            </div>

        </div>

        <!-- Outside ref="content" so it never lands in the PDF -->
        <button class="ft-download" :disabled="capturing" @click="downloadPDF">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.3"
                stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3v12" />
                <path d="M7.5 10.5L12 15l4.5-4.5" />
                <path d="M4.5 20h15" />
            </svg>
            <span>{{ capturing ? 'Preparing…' : 'Download PDF' }}</span>
        </button>
    </div>
</template>

<script>
import './FreetargetStyle.css'

// Inline so each requirement can carry its own mark without three more files.
const CLOCK = '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5.2l3.2 1.9"/></svg>';
const STAR = '<svg width="21" height="21" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"><path d="M12 3.6l2.6 5.4 5.9.8-4.3 4.1 1 5.9-5.2-2.8-5.2 2.8 1-5.9L3.5 9.8l5.9-.8L12 3.6z"/></svg>';
const BEANS = '<svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"><ellipse cx="12" cy="6.6" rx="7.6" ry="3.1"/><path d="M4.4 6.6v10.8c0 1.7 3.4 3.1 7.6 3.1s7.6-1.4 7.6-3.1V6.6"/><path d="M4.4 12c0 1.7 3.4 3.1 7.6 3.1s7.6-1.4 7.6-3.1"/></svg>';

export default {
    name: 'FreetargetUpdate',

    data() {
        return {
            capturing: false,

            requirements: [
                {
                    icon: CLOCK,
                    value: '4 hours a day',
                    text: 'One should do live streaming and call for minimum of 4 hours a day (in total).',
                },
                {
                    icon: STAR,
                    value: '4 stars a week',
                    text: 'For rewards, one should collect a minimum of 4 stars in a week. 1 star = minimum 4 hours live streaming in a day.',
                },
                {
                    icon: BEANS,
                    value: '50,000 beans a week',
                    text: 'Host / live streamer has to make a minimum of 50,000 beans per week to qualify for rewards.',
                },
            ],

            guidelines: [
                'Live streamer background should be neat and clean. For a better background one can use a good background colour.',
                'Live streamer should use Bluetooth for voice clarity and a ring light for excellent beautification effects.',
                'Live streamer should wear smart casuals.',
            ],
        }
    },

    methods: {
        async downloadPDF() {
            const content = this.$refs.content;
            if (!content || this.capturing) return;

            // Flatten the gradients html2canvas cannot paint before capturing.
            this.capturing = true;
            await this.$nextTick();

            try {
                // Loaded on demand so the PDF libraries are only fetched when the
                // user actually taps download, instead of on every page load.
                const [{ default: html2canvas }, { jsPDF }] = await Promise.all([
                    import(/* webpackChunkName: "pdf-export" */ 'html2canvas'),
                    import(/* webpackChunkName: "pdf-export" */ 'jspdf'),
                ]);

                // No windowWidth override: forcing one re-lays the page out
                // during capture and shifted the content left, clipping it.
                const canvas = await html2canvas(content, {
                    scale: 2,
                    useCORS: true,
                    backgroundColor: '#5B0816',
                    logging: false,
                });

                const pdf = new jsPDF('p', 'mm', 'a4');
                const pageWidth = pdf.internal.pageSize.getWidth();
                const pageHeight = pdf.internal.pageSize.getHeight();
                const imgWidth = pageWidth;
                const imgHeight = (canvas.height * imgWidth) / canvas.width;

                // JPEG rather than PNG: the old PNG export ran to ~17 MB.
                const imgData = canvas.toDataURL('image/jpeg', 0.92);

                let heightLeft = imgHeight;
                let position = 0;

                pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;

                while (heightLeft > 0) {
                    pdf.addPage();
                    position = 0 - (imgHeight - heightLeft);
                    pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
                    heightLeft -= pageHeight;
                }

                pdf.save('free-target.pdf');
            } catch (error) {
                console.error('PDF export failed:', error?.message || error);
            } finally {
                this.capturing = false;
            }
        },
    },
}
</script>
